import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateAssociation, useDeleteAssociation } from '@/hooks/use-associations';
import { useFleetServers } from '@/hooks/use-fleet-servers';
import { ApiError } from '@/lib/api-client';
import type { Association, ServerDetail } from '@/types/fleet';

/** Tunnel protocols the API accepts (TunnelProto enum). */
const TUNNEL_PROTOS = ['wireguard', 'amneziawg'];

const SELECT_CLASS = 'h-9 rounded-md border border-input bg-transparent px-2 text-sm';

/**
 * Tunnel-association editor for one server.
 *
 * Shows the server's upstream/downstream edges and lets an operator attach a
 * downstream server (e.g. tag an exit node beneath a VPN server). The edge is
 * created with this server as the upstream.
 */
export function AssociationEditor({ server }: { server: ServerDetail }): JSX.Element {
  const allServers = useFleetServers({});
  const create = useCreateAssociation(server.name);
  const remove = useDeleteAssociation(server.name);

  const [target, setTarget] = useState('');
  const [proto, setProto] = useState('wireguard');
  const [error, setError] = useState<string | null>(null);

  const candidates = (allServers.data?.items ?? []).filter((s) => s.name !== server.name);

  function add(): void {
    setError(null);
    if (target === '') return;

    create.mutate(
      { downstreamName: target, tunnelProto: proto },
      {
        onSuccess: () => {
          toast.success(`Associated ${target} downstream of ${server.name}.`);
          setTarget('');
        },
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : 'Could not create the association.'),
      },
    );
  }

  function removeEdge(upstreamName: string, downstreamName: string): void {
    remove.mutate(
      { upstreamName, downstreamName },
      {
        onSuccess: () => toast.success('Association removed.'),
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not remove the association.'),
      },
    );
  }

  function edgeRow(a: Association, onRemove: () => void): JSX.Element {
    return (
      <li key={a.name} className="flex items-center justify-between">
        <span>
          {a.name}{' '}
          <span className="text-xs text-muted-foreground">({a.tunnel_proto})</span>
        </span>
        <button
          type="button"
          onClick={onRemove}
          disabled={remove.isPending}
          className="text-muted-foreground hover:text-destructive"
          aria-label={`Remove ${a.name}`}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      </li>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tunnel associations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <section>
          <h3 className="mb-1 text-sm font-medium">Upstream</h3>
          {server.associations.upstream.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {server.associations.upstream.map((a) =>
                edgeRow(a, () => removeEdge(a.name, server.name)),
              )}
            </ul>
          )}
        </section>

        <section>
          <h3 className="mb-1 text-sm font-medium">Downstream</h3>
          {server.associations.downstream.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {server.associations.downstream.map((a) =>
                edgeRow(a, () => removeEdge(server.name, a.name)),
              )}
            </ul>
          )}
        </section>

        <div className="space-y-2 border-t pt-3">
          <p className="text-xs text-muted-foreground">
            Attach a downstream server — e.g. tag an exit node beneath this server.
          </p>
          <div className="flex flex-wrap gap-2">
            <select
              className={SELECT_CLASS}
              value={target}
              onChange={(event) => {
                setTarget(event.target.value);
                setError(null);
              }}
            >
              <option value="">Select a server…</option>
              {candidates.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name} ({s.role})
                </option>
              ))}
            </select>
            <select
              className={SELECT_CLASS}
              value={proto}
              onChange={(event) => setProto(event.target.value)}
            >
              {TUNNEL_PROTOS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={add} disabled={target === '' || create.isPending}>
              {create.isPending ? 'Adding…' : 'Associate'}
            </Button>
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
